/**
 * Money helpers.
 *
 * All monetary amounts are stored and transported as INTEGER *minor units*
 * (paise for INR). Floating-point rupee values are only ever parsed from
 * user input at the API boundary and immediately converted.
 */

export function toMinorUnits(amount: number): number {
  return Math.round(amount * 100);
}

export function fromMinorUnits(minor: number): number {
  return minor / 100;
}

/** Indian digit grouping: 1,23,45,678 (last 3 digits, then groups of 2). */
function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  let rest = digits.slice(0, -3);
  const parts: string[] = [];
  while (rest.length > 2) {
    parts.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest.length > 0) parts.unshift(rest);
  return `${parts.join(",")},${last3}`;
}

/**
 * Format minor units as a currency string with Indian digit grouping.
 * Uses "₹" by default in the UI; pass prefix "INR " for the invoice PDF,
 * whose embedded standard fonts cannot render the rupee glyph.
 */
export function formatMoneyMinor(
  minor: number,
  opts: { prefix?: string } = {},
): string {
  const prefix = opts.prefix ?? "₹";
  const negative = minor < 0;
  const abs = Math.abs(Math.round(minor));
  const rupees = Math.floor(abs / 100).toString();
  const paise = (abs % 100).toString().padStart(2, "0");
  const sign = negative ? "\u2212" : "";
  return `${sign}${prefix}${groupIndian(rupees)}.${paise}`;
}

/** Parse a user-entered amount ("12,345.50", "1250") to minor units, or null. */
export function parseAmountToMinor(input: string): number | null {
  const trimmed = input.trim().replace(/,/g, "");
  if (trimmed === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return toMinorUnits(Number(trimmed));
}
